class CustomDetails extends HTMLElement {
  connectedCallback() {
    this.disclosureElement = this.querySelector('details');
    this.summaryElement = this.disclosureElement?.querySelector('summary');
    this.panelElement = this.disclosureElement?.querySelector('.accordion__panel');
    this.contentElement =
      this.panelElement?.querySelector('.accordion__content') ||
      this.disclosureElement?.lastElementChild;

    if (
      !this.disclosureElement ||
      !this.summaryElement ||
      !this.panelElement ||
      !this.contentElement
    ) {
      return;
    }

    this.resetAnimationStyles();

    this.disclosureElement.setAttribute(
      'aria-expanded',
      this.disclosureElement.open ? 'true' : 'false'
    );

    this.onSummaryClicked = this.onSummaryClicked.bind(this);
    this.summaryElement.addEventListener('click', this.onSummaryClicked);

    if (window.Shopify?.designMode) {
      this.onBlockSelect = (event) => this.toggle(true, !event.detail.load);
      this.onBlockDeselect = (event) => this.toggle(false, !event.detail.load);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  disconnectedCallback() {
    this.summaryElement?.removeEventListener('click', this.onSummaryClicked);
    if (window.Shopify?.designMode) {
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  onSummaryClicked(event) {
    event.preventDefault();

    if (
      this.disclosureElement.open &&
      this.summaryElement.hasAttribute('data-follow-link')
    ) {
      window.location.href = this.summaryElement.getAttribute('data-follow-link');
      return;
    }

    this.toggle();
  }

  toggle(force, animate = true) {
    const isOpen = this.disclosureElement.getAttribute('aria-expanded') === 'true';
    const shouldOpen = typeof force === 'boolean' ? force : !isOpen;

    if (shouldOpen) {
      this.open({ instant: !animate });
    } else {
      this.close({ instant: !animate });
    }
  }

  open({ instant = false } = {}) {
    if (this.disclosureElement.getAttribute('aria-expanded') === 'true') {
      return;
    }

    this.cancelAnimations();
    this.disclosureElement.open = true;
    this.disclosureElement.setAttribute('aria-expanded', 'true');

    const controls = this.createShowAnimationControls();
    if (instant && controls?.finish) {
      controls.finish();
    }
  }

  close({ instant = false } = {}) {
    this.disclosureElement.setAttribute('aria-expanded', 'false');

    if (!this.disclosureElement.open) {
      return;
    }

    this.cancelAnimations();

    const controls = this.createHideAnimationControls();
    if (instant && controls?.finish) {
      controls.finish();
      this.disclosureElement.removeAttribute('open');
      this.resetAnimationStyles();
      return;
    }

    controls?.finished?.then(() => {
      this.disclosureElement.removeAttribute('open');
      this.resetAnimationStyles();
    });
  }

  cancelAnimations() {
    this.panelElement?.getAnimations().forEach((animation) => animation.cancel());
    this.contentElement?.getAnimations().forEach((animation) => animation.cancel());
    this.disclosureElement?.getAnimations().forEach((animation) => animation.cancel());
  }

  resetAnimationStyles() {
    if (this.panelElement) {
      this.panelElement.style.height = '';
      this.panelElement.style.overflow = '';
    }

    if (this.contentElement) {
      this.contentElement.style.opacity = '';
      this.contentElement.style.transform = '';
    }

    if (this.disclosureElement) {
      this.disclosureElement.style.height = '';
      this.disclosureElement.style.overflow = '';
    }
  }

  createShowAnimationControls() {
    return null;
  }

  createHideAnimationControls() {
    return null;
  }
}

class AccordionDisclosure extends CustomDetails {
  createShowAnimationControls() {
    const panel = this.panelElement;
    const content = this.contentElement;

    panel.style.overflow = 'hidden';
    panel.style.height = 'auto';
    const toHeight = panel.scrollHeight;
    panel.style.height = '0px';

    const heightAnimation = panel.animate(
      [{ height: '0px' }, { height: `${toHeight}px` }],
      { duration: 250, easing: 'ease', fill: 'forwards' }
    );

    const contentAnimation = content.animate(
      [
        { opacity: 0, transform: 'translateY(4px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      { duration: 150, delay: 140, fill: 'forwards' }
    );

    const finished = Promise.all([heightAnimation.finished, contentAnimation.finished]).then(() => {
      this.resetAnimationStyles();
    });

    const resetStyles = () => this.resetAnimationStyles();

    return {
      finished,
      finish() {
        heightAnimation.finish();
        contentAnimation.finish();
        resetStyles();
      },
    };
  }

  createHideAnimationControls() {
    const panel = this.panelElement;
    const content = this.contentElement;

    panel.style.overflow = 'hidden';
    const fromHeight = panel.scrollHeight;
    panel.style.height = `${fromHeight}px`;

    const contentAnimation = content.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: 150,
      fill: 'forwards',
    });

    const heightAnimation = panel.animate(
      [{ height: `${fromHeight}px` }, { height: '0px' }],
      { duration: 250, easing: 'ease', fill: 'forwards' }
    );

    const resetStyles = () => this.resetAnimationStyles();

    const finished = Promise.all([contentAnimation.finished, heightAnimation.finished]).then(() => {
      resetStyles();
      return true;
    });

    return {
      finished,
      finish() {
        contentAnimation.finish();
        heightAnimation.finish();
        resetStyles();
      },
    };
  }
}

if (!customElements.get('accordion-disclosure')) {
  customElements.define('accordion-disclosure', AccordionDisclosure);
}
